import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// ── Query key factories ──

export const userKeys = {
  all: ["users"] as const,
  list: (filters?: Record<string, unknown>) => ["users", "list", filters ?? {}] as const,
  detail: (userId: string) => ["users", "detail", userId] as const,
  /** Client Accounts this user owns — the §5 `ownAccount` scope's source of truth. */
  accounts: (userId: string) => ["users", "accounts", userId] as const,
  /** CRM-derived ownership suggestions for this user. */
  suggestions: (userId: string) => ["users", "suggestions", userId] as const,
} as const;

/** Automatic role-mapping rules (Settings → Users). */
export const userRoleRuleKeys = {
  all: ["user-role-rules"] as const,
  list: () => ["user-role-rules", "list"] as const,
} as const;

export const disciplineKeys = {
  all: ["disciplines"] as const,
  list: (opts?: { includeInactive?: boolean }) => ["disciplines", "list", opts ?? {}] as const,
} as const;

// GET /api/reports/pipeline returns the full unpaginated result set — sort/filter/paging
// all live client-side over that one array (see src/api/pipeline-report.ts), so there is
// no params variant to key by.
export const pipelineReportKeys = {
  all: ["pipeline-report"] as const,
  list: () => ["pipeline-report", "list"] as const,
} as const;

// GET /api/reports/plan-rejections returns the whole (scoped) log in one call — same
// convention as pipelineReportKeys above.
export const planRejectionKeys = {
  all: ["plan-rejections"] as const,
  list: () => ["plan-rejections", "list"] as const,
} as const;

// GET /api/reports/decision-log returns the full unpaginated result set — same
// convention as pipelineReportKeys above, for the same reason.
export const decisionLogKeys = {
  all: ["decision-log"] as const,
  list: () => ["decision-log", "list"] as const,
} as const;

// Per-level help text for a Discipline's fixed 1–5 rating scale (distinct from
// disciplineRatingKeys, which caches per-Resource rating values).
export const disciplineRatingLevelKeys = {
  all: ["discipline-rating-levels"] as const,
  list: (disciplineId: string) => ["discipline-rating-levels", "list", disciplineId] as const,
} as const;

export const podShapeKeys = {
  all: ["pod-shapes"] as const,
  list: (opts?: { includeInactive?: boolean }) => ["pod-shapes", "list", opts ?? {}] as const,
  tiers: () => ["pod-shapes", "tiers"] as const,
} as const;

export const resourceDisciplineKeys = {
  all: ["resource-disciplines"] as const,
  list: (resourceId: string) => ["resource-disciplines", "list", resourceId] as const,
} as const;

export const disciplineRatingKeys = {
  all: ["discipline-ratings"] as const,
  list: (resourceId: string) => ["discipline-ratings", "list", resourceId] as const,
} as const;

export const engagementStandardTierKeys = {
  all: ["engagement-standard-tiers"] as const,
  /**
   * Settings ▸ Tiers — the full per-tier configuration.
   *
   * NOTE the older, narrower tier list is cached under `podShapeKeys.tiers()`, not here:
   * it predates this factory and the Accounts tier picker plus the Shapes page both read
   * it. Any tier MUTATION must invalidate both keys or those screens go stale.
   */
  list: () => ["engagement-standard-tiers", "list"] as const,
} as const;

export const solutionPodPositionKeys = {
  all: ["solution-pod-positions"] as const,
  list: (solutionId: string) => ["solution-pod-positions", "list", solutionId] as const,
} as const;

export const adminConstraintKeys = {
  all: ["admin", "constraints"] as const,
  list: () => ["admin", "constraints", "list"] as const,
  groups: () => ["admin", "constraints", "groups"] as const,
} as const;

export const adminIntensityLevelKeys = {
  all: ["admin", "intensity-levels"] as const,
  list: () => ["admin", "intensity-levels", "list"] as const,
} as const;

export const staffingRequestPriorityKeys = {
  all: ["staffing-request-priorities"] as const,
  list: () => ["staffing-request-priorities", "list"] as const,
} as const;

export const staffingRequestKeys = {
  all: ["staffing-requests"] as const,
  list: (params?: {
    statuses?: string[];
    kinds?: string[];
    priorityIds?: string[];
    cursor?: string;
    direction?: string;
    limit?: number;
  }) => ["staffing-requests", "list", params ?? {}] as const,
  detail: (id: string) => ["staffing-requests", "detail", id] as const,
  solutionOptions: () => ["staffing-requests", "solution-options"] as const,
  resourceOptions: (solutionId: string, search: string) =>
    ["staffing-requests", "resource-options", solutionId, search] as const,
  swapCandidates: (solutionId: string, search: string) =>
    ["staffing-requests", "swap-candidates", solutionId, search] as const,
  openRoles: () => ["staffing-requests", "open-roles"] as const,
} as const;

export const integrationSolutionKeys = {
  all: ["integration-solutions"] as const,
  list: () => ["integration-solutions", "list"] as const,
} as const;

export const integrationAccountKeys = {
  all: ["integration-accounts"] as const,
  list: () => ["integration-accounts", "list"] as const,
} as const;

export const integrationDemandLinkKeys = {
  all: ["integration-demand-links"] as const,
  list: () => ["integration-demand-links", "list"] as const,
} as const;

export const integrationResourceKeys = {
  all: ["integration-resources"] as const,
  list: () => ["integration-resources", "list"] as const,
} as const;

export const integrationTimeEntryKeys = {
  all: ["integration-time-entries"] as const,
  list: () => ["integration-time-entries", "list"] as const,
} as const;

/** The Resources directory payload (GET /api/dashboard/resources) — shared by every screen that lists Resources. */
export const resourceDirectoryKeys = {
  all: ["dashboard-resources"] as const,
} as const;

/** Demands page: the scoped NOS Demand list and one Demand's six-month grid. */
export const demandKeys = {
  all: ["demands"] as const,
  list: () => ["demands", "list"] as const,
  detail: (demandId: string, windowStart: string | null) =>
    ["demands", "detail", demandId, windowStart ?? "default"] as const,
} as const;

/** Reports section — hours reports (src/api/hours-reports.ts). Keyed on the query string. */
export const hoursReportKeys = {
  all: ["hours-reports"] as const,
  demandByAccount: (qs: string) => ["hours-reports", "demand-by-account", qs] as const,
  resourceDirectory: () => ["hours-reports", "resource-directory"] as const,
  resource: (personId: string, qs: string) =>
    ["hours-reports", "resource", personId.toLowerCase(), qs] as const,
} as const;

export const guidanceKeys = {
  all: ["guidance"] as const,
  list: () => ["guidance", "list"] as const,
  /** One Resource's Solutions × months guidance/actuals matrix (drawer drill-down). */
  matrix: (resourceId: string) => ["guidance", "matrix", resourceId] as const,
} as const;

export const accountKeys = {
  all: ["accounts"] as const,
  list: (params?: { cursor?: string; direction?: string; search?: string; limit?: number }) =>
    ["accounts", "list", params ?? {}] as const,
  detail: (id: string) => ["accounts", "detail", id] as const,
  tierImpact: (id: string, tierId?: string) =>
    ["accounts", "tier-impact", id, tierId ?? "current"] as const,
  constraints: (id: string) => ["accounts", "constraints", id] as const,
  constraintLibrary: () => ["accounts", "constraint-library"] as const,
  /**
   * RP-12.4 — this Account's negotiated Rate Card (overrides on the firm-wide card).
   * `tierId` is the tier the card is priced against; it varies while the user tries a
   * different tier before saving, so it belongs in the key or the preview would serve
   * the previous tier's cached rates.
   */
  accountRateCard: (id: string, tierId?: string, basis?: string) =>
    ["accounts", "rate-card", id, tierId ?? "saved", basis ?? "saved"] as const,
  /** Every tier variant of one Account's rate card — what a mutation must invalidate. */
  accountRateCardAll: (id: string) => ["accounts", "rate-card", id] as const,
  /**
   * Which Rate Card a new plan on this Solution would price off (the New Resource Plan
   * form). Keyed by Solution, so a rate change invalidates the whole family.
   */
  solutionPlanRateCard: (solutionId: string) => ["accounts", "plan-rate-card", solutionId] as const,
  solutionPlanRateCardAll: ["accounts", "plan-rate-card"] as const,
} as const;

export const resourceEngagementsKeys = {
  all: ["resource-engagements"] as const,
  detail: (resourceId: string) => ["resource-engagements", "detail", resourceId] as const,
} as const;

export const solutionWorkloadKeys = {
  all: ["solution-workload"] as const,
  detail: (solutionId: string) => ["solution-workload", "detail", solutionId] as const,
} as const;

export const solutionDemandLinkKeys = {
  all: ["solution-demand-links"] as const,
  detail: (solutionId: string) => ["solution-demand-links", "detail", solutionId] as const,
} as const;

export const solutionPipelineKeys = {
  all: ["solution-pipeline"] as const,
  detail: (solutionId: string) => ["solution-pipeline", "detail", solutionId] as const,
} as const;

// ── Resource Plan M1 — Configuration spine (docs/specs/resource-plan.md) ──────

export const laborCategoryKeys = {
  all: ["labor-categories"] as const,
  list: () => ["labor-categories", "list"] as const,
} as const;

// Configuration → Internal Rates (2026-10-14). Keyed by the step-up proof too: a new
// proof is a new visit, and a cached page must never outlive the proof that fetched it.
export const internalRateKeys = {
  all: ["resource-internal-rates"] as const,
  list: (
    stepUpId: string,
    params: { search: string; withoutRate: boolean; cursor: string | null },
  ) => ["resource-internal-rates", "list", stepUpId, params] as const,
  history: (stepUpId: string, resourceId: string) =>
    ["resource-internal-rates", "history", stepUpId, resourceId] as const,
} as const;

export const stepUpKeys = {
  options: () => ["step-up", "options"] as const,
} as const;

export const rateCardKeys = {
  all: ["rate-cards"] as const,
  current: () => ["rate-cards", "current"] as const,
  incompleteBases: () => ["rate-cards", "incomplete-bases"] as const,
} as const;

// ── Resource Plan M2 — Plan on a Solution (docs/specs/resource-plan.md) ──────

/**
 * RP-32.4 — Resource Plan comments (line notes; RP-34's Concern replies reuse it). Under
 * `resourcePlanKeys.all`, so any plan invalidation refreshes them too.
 */
export const planCommentKeys = {
  all: ["resource-plans", "comments"] as const,
  list: (planId: string, scope: { planLineId?: string }) =>
    ["resource-plans", "comments", planId, scope] as const,
};

export const resourcePlanKeys = {
  all: ["resource-plans"] as const,
  list: (filters?: { solutionId?: string; clientAccountId?: string; status?: string }) =>
    ["resource-plans", "list", filters ?? {}] as const,
  detail: (planId: string) => ["resource-plans", "detail", planId] as const,
  /** RP-25: every page and filter of one plan's Changes tab — invalidate by this. */
  changesFor: (planId: string) => ["resource-plans", "changes", planId] as const,
  changes: (
    planId: string,
    filters: {
      role?: string;
      planLineId?: string;
      commercial?: boolean;
      from?: string;
      to?: string;
    },
  ) => ["resource-plans", "changes", planId, filters] as const,
  /** RP-32.2 — one Plan Line's Candidates (shortlist + history). */
  candidates: (lineId: string) => ["resource-plans", "candidates", lineId] as const,
  /** RP-33: the Plan Line names the question (Discipline, rating range, Position, months). */
  picker: (planId: string, search: string, lineId: string, showAll: boolean) =>
    ["resource-plans", "picker", planId, search, lineId, showAll] as const,
  /** The read-only Plan Overview a plan is shared as (bill-side only, no cost/margin). */
  shared: (planId: string) => ["resource-plans", "shared", planId] as const,
  /**
   * The grid's NOS guidance/capacity overlay (eye + load bar). Keyed on the
   * plan's distinct suggested Resource ids (sorted, deduped) so the overlay refetches
   * the moment that set changes — the endpoint reads it from SAVED plan lines, which
   * only move on a grid save or a suggested-Resource mutation. Nested under
   * `["resource-plans", ...]` so it is also swept up by the broad `resourcePlanKeys.all`
   * invalidation every plan mutation already performs (`useInvalidatePlans`).
   */
  guidanceLoad: (planId: string, suggestedResourceIds: readonly string[]) =>
    ["resource-plans", "guidance-load", planId, [...new Set(suggestedResourceIds)].sort()] as const,
  /** Each line's suggested Resource's Fit Score — keyed on who is suggested where. */
  lineFits: (planId: string, lineResources: readonly string[]) =>
    ["resource-plans", "line-fits", planId, [...lineResources].sort()] as const,
  /**
   * This plan's rates proposed as its Account's Rate Card. Under the plan prefix on
   * purpose: a plan mutation invalidating `resourcePlanKeys.all` also refreshes the
   * proposal, so the rates it would save never lag the grid.
   */
  accountRateCard: (planId: string) => ["resource-plans", "account-rate-card", planId] as const,
  /**
   * The card this plan prices from, its history per Labor Category, and what a tier change
   * would do (RP-6.6 / RP-12.6 / RP-12.8). Under the plan prefix so any plan mutation —
   * a line added, a rate overridden — refreshes the "rates on this plan" column too.
   */
  rateCard: (planId: string) => ["resource-plans", "rate-card", planId] as const,
  rateHistory: (planId: string, laborCategoryId: string) =>
    ["resource-plans", "rate-card", planId, "history", laborCategoryId] as const,
  tierPreview: (planId: string, tierId: string, alsoSetSolutionTier: boolean) =>
    ["resource-plans", "rate-card", planId, "tier-preview", tierId, alsoSetSolutionTier] as const,
  /** Candidates for the plan Owner/Author picker (`PlanOwnerDialog`). Not scoped to a
   * plan — it is "who may own a plan", not "who owns this one". */
  ownerOptions: () => ["resource-plans", "owner-options"] as const,
  /** What setting the plan to Staffed would write onto its Solution (the confirm dialog). */
  staffingPreview: (planId: string) => ["resource-plans", "staffing-preview", planId] as const,
  /** Planned vs actual on an Active plan (the Actuals tab). */
  actuals: (planId: string) => ["resource-plans", "actuals", planId] as const,
  /**
   * RP-28: what a Move or Fit to new dates would do. Deliberately OUTSIDE the
   * `["resource-plans", ...]` prefix: applying the change invalidates that prefix, and a
   * preview refetched for dates the plan now has would only come back "those are the
   * current dates". Keyed on the plan's `updatedAt`, so any edit still gets a fresh one.
   */
  datesPreview: (
    planId: string,
    planUpdatedAt: string,
    request: { mode: string; startMonth: string; endMonth?: string | null },
  ) =>
    [
      "plan-dates-preview",
      planId,
      planUpdatedAt,
      request.mode,
      request.startMonth,
      request.endMonth ?? "",
    ] as const,
  /** RP-28.3: whether a Staffed plan's dates moved since staffing (the RM's notice). */
  datesStaffingReview: (planId: string) =>
    ["resource-plans", "dates-staffing-review", planId] as const,
  /** RP-34 Staffing Concerns on a plan — under the plan prefix, so plan invalidation sweeps it. */
  concerns: (planId: string) => ["resource-plans", "concerns", planId] as const,
  /** RP-34.1 — the plan's Author, Account Owners and CRM contacts. */
  accountTeam: (planId: string) => ["resource-plans", "account-team", planId] as const,
} as const;

/** RP-34 — the signed-in user's own in-app notifications. */
export const notificationKeys = {
  all: ["notifications"] as const,
  /** The bell: the newest unread notices and the unread count. */
  unread: () => ["notifications", "unread"] as const,
  /** The full-screen inbox, a page at a time: "new" (unread) or "all". */
  inbox: (view: "new" | "all") => ["notifications", "inbox", view] as const,
} as const;

/** Reported Issues — the reporter's own list, the ADMIN queue, and one Issue. */
export const supportIssueKeys = {
  all: ["support-issues"] as const,
  list: (params: Record<string, unknown>) => ["support-issues", "list", params] as const,
  detail: (id: string) => ["support-issues", "detail", id] as const,
  assignees: () => ["support-issues", "assignees"] as const,
} as const;

// ── Resource Plan M3 — Level 1 estimating and the Planned Pod ────────────────
//
// The plan DETAIL carries the Planned Pod, the effective mix and the ramp, so there is
// deliberately no separate key for any of them: every M3 mutation returns the whole
// re-shaped detail view and invalidating `resourcePlanKeys.all` refreshes all three at
// once. Only the two things that are NOT part of a plan get keys of their own.
//
// Estimated Hours Lines follow the same rule: `estimatedHoursLines` rides along on
// `ResourcePlanDetailView` (src/api/estimated-hours-lines.ts), so its mutations
// (src/hooks/use-estimated-hours-lines.ts) invalidate `resourcePlanKeys.all` too — no
// key of its own.

// ── Resource Plan M4 — Governance (Epics 8, 17, 10) ─────────────────────────
//
// Unlike M3, these DO get keys of their own: a version list, an approval and a comparison
// are not part of the plan detail and are not refreshed by a plan mutation returning the
// re-shaped plan. The plan detail carries only the three governance COUNTS the header
// needs (`versionNumber`, `pendingApprovalId`, `frozenVersionCount`), which is why a
// freeze or a decision has to invalidate both.

export const planVersionKeys = {
  all: ["plan-versions"] as const,
  forPlan: (planId: string) => ["plan-versions", "plan", planId] as const,
  detail: (versionId: string) => ["plan-versions", "detail", versionId] as const,
  changeOrderRefs: (planId: string) => ["plan-versions", "change-order-refs", planId] as const,
} as const;

export const planCompareKeys = {
  all: ["plan-compare"] as const,
  pair: (left: string, right: string) => ["plan-compare", left, right] as const,
} as const;

export const planApprovalKeys = {
  all: ["plan-approvals"] as const,
  forPlan: (planId: string) => ["plan-approvals", "plan", planId] as const,
  detail: (approvalId: string) => ["plan-approvals", "detail", approvalId] as const,
  queue: () => ["plan-approvals", "queue"] as const,
} as const;

export const planExportKeys = {
  all: ["plan-export"] as const,
  options: (planId: string) => ["plan-export", "options", planId] as const,
} as const;

export const workstreamTemplateKeys = {
  all: ["workstream-templates"] as const,
  list: (includeSuperseded: boolean) =>
    ["workstream-templates", "list", includeSuperseded] as const,
} as const;

export const planShapeKeys = {
  all: ["plan-pod-shapes"] as const,
  forPlan: (planId: string) => ["plan-pod-shapes", planId] as const,
} as const;

export const planningConstantKeys = {
  all: ["planning-constants"] as const,
  list: () => ["planning-constants", "list"] as const,
} as const;

export const systemConfigKeys = {
  all: ["system-config"] as const,
  hoursPerIntensityPoint: () => ["system-config", "hours-per-intensity-point"] as const,
  solutionDeletionEnabled: () => ["system-config", "solution-deletion-enabled"] as const,
} as const;

export const systemLogKeys = {
  all: ["system-logs"] as const,
  list: (params?: {
    cursor?: string;
    direction?: string;
    level?: string;
    category?: string;
    status?: string;
    method?: string;
    path?: string;
    from?: string;
    to?: string;
    limit?: number;
  }) => ["system-logs", "list", params ?? {}] as const,
  detail: (id: string) => ["system-logs", "detail", id] as const,
} as const;

export const solutionDashboardKeys = {
  all: ["dashboard-solutions"] as const,
  deletePreview: (ids: string[]) => ["dashboard-solutions", "delete-preview", ...ids] as const,
} as const;

export const signalKeys = {
  all: ["signals"] as const,
  list: (filter?: { type?: string; accountId?: string }) =>
    ["signals", "list", filter ?? {}] as const,
  stats: (window: number) => ["signals", "stats", window] as const,
  forSolution: (solutionId: string) => ["signals", "solution", solutionId] as const,
} as const;

/**
 * The Solution Detail drawer's own `["solution-detail", id]` query. Factored here
 * so mutations that change a Solution (e.g. confirming a Signal's Intensity) can
 * invalidate the open drawer without hard-coding the key at the call site.
 */
export const solutionDetailKeys = {
  all: ["solution-detail"] as const,
  detail: (id: string) => ["solution-detail", id] as const,
} as const;

// ── Solution estimate integration ────────────────────────────────────────────

export const solutionEstimateProjectKeys = {
  all: ["solution-estimate-projects"] as const,
  span: (projectId: string) => ["solution-estimate-projects", "span", projectId] as const,
} as const;

// ── NOS Deal picker (New Resource Plan form) ─────────────────────────────────

export const planDealOptionKeys = {
  all: ["plan-deal-options"] as const,
} as const;

export const solutionEstimateRoleKeys = {
  all: ["solution-estimate-roles"] as const,
  list: () => ["solution-estimate-roles", "list"] as const,
} as const;

// ── Resource Rundown configuration ───────────────────────────────────────────

export const rundownConfigKeys = {
  all: ["rundown-config"] as const,
  detail: () => ["rundown-config", "detail"] as const,
  nonDeliveryHomeRoles: () => ["rundown-config", "non-delivery-home-roles"] as const,
} as const;

/**
 * The Availability report: the grid payload for a window, and one person-month's
 * Solutions (tooltip + drill-in panel). Saving the Availability cut-offs or the
 * non-delivery Home Roles invalidates `all`, so the grid reclassifies.
 */
export const availabilityKeys = {
  all: ["availability"] as const,
  report: (query: { from: string; to: string; includeProposed: boolean }) =>
    ["availability", "report", query.from, query.to, query.includeProposed] as const,
  resourceMonth: (resourceId: string, month: string) =>
    ["availability", "resource-month", resourceId, month] as const,
} as const;

/**
 * The Resources page heatmap (`ResourceHeatmap`). Invalidated by anything that moves
 * a Resource's load or a Solution's Fit — assignments, points, and the Solution's
 * Constraint list.
 */
export const dashboardHeatmapKeys = {
  all: ["dashboard-heatmap"] as const,
} as const;

/**
 * Atlas candidate suggestions for one of a Solution's Pod Positions (`PodSlot`'s
 * picker). Every candidate carries a `coverage` computed against the Solution's
 * Constraints, so a Constraint add/remove invalidates the whole `forSolution` family.
 */
export const atlasSuggestKeys = {
  all: ["atlas-suggest"] as const,
  forSolution: (solutionId: string) => ["atlas-suggest", solutionId] as const,
  forRole: (solutionId: string, role: string) => ["atlas-suggest", solutionId, role] as const,
} as const;

/** The running app's version and release summary, shown in the bottom status bar. */
export const appInfoKeys = {
  all: ["app-info"] as const,
} as const;

/**
 * A Resource Plan's NOS Deal Stage and Chance of Close (spec Epic 36). Under the
 * `["resource-plans", ...]` prefix on purpose: every plan mutation's broad
 * `resourcePlanKeys.all` invalidation refreshes it too — a status change moves its
 * "plan and Deal disagree" warning.
 */
export const planDealStageKeys = {
  detail: (planId: string) => ["resource-plans", "deal-stage", planId] as const,
} as const;

/** A plan's NOS Demand: the Account's Demands to pick from, and the Post to NOS preview.
 *  Under "resource-plans" so every plan invalidation refreshes them too. */
export const planNosDemandKeys = {
  options: (planId: string) => ["resource-plans", "nos-demand", planId, "options"] as const,
  linkPreview: (planId: string, demandId: string) =>
    ["resource-plans", "nos-demand", planId, "link-preview", demandId] as const,
  postPreview: (planId: string) =>
    ["resource-plans", "nos-demand", planId, "post-preview"] as const,
} as const;
